import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import { Dropdown } from 'primereact/dropdown'
import { FilterMatchMode, FilterService } from 'primereact/api'
import { TriStateCheckbox } from 'primereact/tristatecheckbox'

import { capitalizeFirstLetter, dynamicCustomFilter } from '../../utility/genricFunction'
import { permissions, ROUTES_PATH, UI_CONSTANT } from '../../utility/constants'
import Button from '../../components/button/Button'
import PopupBox from '../../components/popup/PopupBox'
import BreadCrumbs from '../../components/breadcrumb/Breadcrumb'
import PageDropdown from '../../components/page-dropdown/PageDropdown'
import ParentDataTable from '../../components/data-table/ParentDataTable'

import { getRoles } from '../../store/builders/roleManagement/rolemanagement.builder.js'
import pencilIcon from '../../assets/icons/pencil1.svg'
import HorizontalLine from '../../assets/icons/Line33.svg'
import UseAuthorization from '../../utility/useAuthorization.js'
import Loader from '../../components/loader/Loader.jsx'


function RoleManagement() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const roleSelector = useSelector((store) => store.auth)
  const userRole = roleSelector?.user?.result?.userData?.roleName
  const { loading, roleData } = useSelector((store) => store?.roleManagement)
  const popupRef = useRef(null)
  const [isOpen, setIsOpen] = useState(false)

  const roleTableData = roleData?.roles

  const numbers = [
    { label: '10', value: 10 },
    { label: '25', value: 25 },
    { label: '50', value: 50 },
  ]

  const [selectedNumber, setSelectedNumber] = useState(numbers[0].value)

  const [filteredData, setFilteredData] = useState([])
  const [filterNumber] = useState(false)

  FilterService.register('dynamic_custom', (value, filter, filterMeta) => {
    return dynamicCustomFilter(value, filter, filterMeta?.matchMode || 'CONTAINS')
  })

  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    roleName: { value: null, matchMode: 'dynamic_custom' },
    roleStatus: { value: null, matchMode: FilterMatchMode.EQUALS },
  })

  const [selectedValue, setSelectedValue] = useState(null)


  const statuses = [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
  ]

  useEffect(() => {
    dispatch(getRoles())
    return () => dispatch({ type: 'roleManagement/clearExtraReducers' })
  }, [])

  useEffect(() => {
    setFilteredData(roleTableData)
  }, [roleTableData])

  const onFilter = (event) => {
    const newFilters = { ...filters, ...event.filters }
    setFilters(newFilters)
    const filtered = filterData(roleTableData, newFilters)
    setFilteredData(filtered)
  }

  const filterData = (data, filters) => {
    return data.filter((item) => {
      return Object.keys(filters).every((key) => {
        if (!filters[key].value) return true
        const value = item[key]
        const filterValue = filters[key].value.toLowerCase().trim()
        return value && value.toString().toLowerCase().includes(filterValue)
      })
    })
  }


  const verifiedBodyTemplate = (roleTableData) => {
    const handleRowClick = (id) => {
      const path = ROUTES_PATH.EDIT_ROLE.replace(':id', id)
      navigate(path)
    }
    return (
      <>
        <PopupBox rowData={roleTableData} setIsOpen={setIsOpen} isOpen={isOpen} popupRef={popupRef}>
          <div className='popupbox'>
            <p className='verifiedBodyTemplate-Assign'>ACTION</p>

            {UseAuthorization(permissions.EDIT_ROLE) && <>
              <img src={HorizontalLine} alt='' />
            <button className='div-assign-to-branch' disabled={roleTableData?.roleStatus == 'archive' ? true : false} onClick={() => handleRowClick(roleTableData.roleId)}>
              <img src={pencilIcon} alt='' />
              <p
                className={`${roleTableData?.roleStatus == 'archive' ? 'verifiedBodyTemplate-darkfont popup-font' : 'verifiedBodyTemplate-darkfont'}`}
              >
                Edit
              </p>
            </button>
            </>}

            

            {/* <button className='div-assign-to-branch' disabled={roleTableData?.branchStatus == 'archive' ? true : false} onClick={handleArchive}>
              <img src={ArchiveIcon} alt='' />
              <p className={`${roleTableData?.roleStatus == 'archive' ? 'verifiedBodyTemplate-darkfont popup-font' : 'verifiedBodyTemplate-darkfont'}`}>Archive</p>
            </button> */}
          </div>
        </PopupBox>
      </>
    )
  }

  const verifiedRowFilterTemplate = (options) => {
    return <TriStateCheckbox value={options.value} onChange={(e) => options.filterApplyCallback(e.value)} />
  }

  const nameBody = (roleTableData) => {
    const representative = roleTableData?.roleName && capitalizeFirstLetter(roleTableData?.roleName)
    return (
      <div className='flex align-items-center gap-2'>
        <span>{representative}</span>
      </div>
    )
  }

  const statusRowFilterTemplate = (options) => {
    const onDropdownChange = (e) => {
      setSelectedValue(e.value)
      options.filterApplyCallback(e.value)
    }

    return (
      <Dropdown
        value={selectedValue}
        options={statuses}
        onChange={onDropdownChange}
        placeholder='All'
        className='p-column-filter'
        optionLabel='label'
        showClear
      />
    )
  }

  const statusBody = (roleTableData) => {
    const representative = roleTableData?.roleStatus && capitalizeFirstLetter(roleTableData?.roleStatus)
    return (
      <div className='flex align-items-center gap-2'>
        <span>{representative}</span>
      </div>
    )
  }

  const columns = [
    {
      sortable: true,
      field: 'roleName',
      header: 'Role name',
      filter: true,
      filterDisplay: 'row',
      filterPlaceholder: 'Search by role name...',
      style: { minWidth: '12rem' },
      body: nameBody,
    },
    {
      sortable: true,
      field: 'userCount',
      header: 'Count',
      showFilterMenu: false,
      filterMenuStyle: { minWidth: '5rem' },
      style: { minWidth: '10rem' },
    },
    {
      sortable: true,
      field: 'roleStatus',
      header: 'Status',
      showFilterMenu: false,
      filterMenuStyle: { minWidth: '5rem' },
      style: { minWidth: '10rem' },
      body: statusBody,
      filter: true,
      filterElement: statusRowFilterTemplate,
    },
    {
      sortable: false,
      field: 'verified',
      header: '',
      dataType: 'boolean',
      style: { maxWidth: '4rem' },
      body: verifiedBodyTemplate,
      filter: false,
      filterElement: verifiedRowFilterTemplate,
    },
  ]

  const breadCrumbsArr = [
    {
      title: UI_CONSTANT.ROLE_MANAGEMENT.LABEL_ROLL_MANAGEMENT,
      link: null,
    },
    {
      title: UI_CONSTANT.DASHBOARD,
      link: ROUTES_PATH.DASHBOARD,
    },
    {
      title: UI_CONSTANT.ROLE_MANAGEMENT.LABEL_ROLL_MANAGEMENT,
      link: ROUTES_PATH.ROLE_MANAGEMENT,
    },
  ]

  return (
    <>
      {loading && <Loader loading={loading} />}

     {<Loader loading={loading} /> &&  <div className={`course-list }`}>
        <div className='filter-bar-wraper'>
          <div className='subheader'>
            <BreadCrumbs breadCrumbsArr={breadCrumbsArr} />

          {UseAuthorization(permissions.ADD_ROLE) && <Button
              className='add-btn'
              onClick={() => navigate('/role-management/add-role')}
            >
              {UI_CONSTANT.ROLE_MANAGEMENT.ADD_ROLE}
            </Button>}
            


          </div>
        </div>

        <div className='group-parent'>
          <div className='frame-wrapper'>
            <div className='course-list-wrapper'>
              <b className='course-list1'>{UI_CONSTANT.ROLE_MANAGEMENT.ALL_ROLE}</b>
            </div>
          </div>
          <div className='flex page-drop-div'>
            <p className='page-sidetext'>{UI_CONSTANT.TABLE_HEADER_SAW}</p>
            <PageDropdown
              options={numbers}
              selectedValue={selectedNumber}
              onChange={(e) => setSelectedNumber(e.value)}
              className='w-full md:w-14rem small-dropdown'
            />
            <p className='page-endtext'>{UI_CONSTANT.TABLE_HEADER_ENTRIES}</p>
          </div>

          <div className={`table-card  ${userRole == UI_CONSTANT.ADMIN_ROLE ? 'action-btn-include' : ''}`}>
            <ParentDataTable
              value={filteredData}
              rows={filterNumber ? filteredData?.length : selectedNumber}
              filters={filters}
              // loading={loading}
              columns={columns}
              archiveKey={'branchStatus'}
              onFilter={onFilter}
            />
          </div>
        </div>
      </div>}
    </>
  )
}

export default RoleManagement
